if (!customElements.get('yb-region-selector')) {
  const isCountryCode = (code) => /^[A-Z]{2}$/.test(code) && code !== 'XX';
  const getCountryFlag = (code) => isCountryCode(code)
    ? String.fromCodePoint(...[...code].map((letter) => letter.charCodeAt(0) + 127397)) : '🌐';
  const configuredLanguageCodes = Array.isArray(window.YB_AUTO_LOCALIZATION?.availableLanguages)
    ? window.YB_AUTO_LOCALIZATION.availableLanguages.map((languageCode) => String(languageCode)).filter(Boolean)
    : ['en'];
  const availableLanguageLookup = new Map(
    configuredLanguageCodes.map((languageCode) => [languageCode.toLowerCase(), languageCode])
  );

  const getAvailableLanguageCode = (preferredLanguage = 'en') => {
    const normalizedPreferredLanguage = String(preferredLanguage || 'en').toLowerCase();

    if (availableLanguageLookup.has(normalizedPreferredLanguage)) {
      return availableLanguageLookup.get(normalizedPreferredLanguage);
    }

    if (normalizedPreferredLanguage.split('-')[0] === 'zh') {
      const traditional = /(?:^|-)(?:hant|tw|hk|mo)(?:-|$)/.test(normalizedPreferredLanguage);
      const aliases = traditional ? ['zh-tw', 'zh-hant'] : ['zh-cn', 'zh-hans', 'zh'];
      for (const alias of aliases) {
        if (availableLanguageLookup.has(alias)) return availableLanguageLookup.get(alias);
      }
      return availableLanguageLookup.get('en') || 'en';
    }

    if (normalizedPreferredLanguage.includes('-')) {
      const baseLanguage = normalizedPreferredLanguage.split('-')[0];
      if (availableLanguageLookup.has(baseLanguage)) {
        return availableLanguageLookup.get(baseLanguage);
      }
    }

    return availableLanguageLookup.get('en') || 'en';
  };

  const parseJsonData = (value, fallback) => {
    try {
      return JSON.parse(String(value || ''));
    } catch (error) {
      return fallback;
    }
  };

  customElements.define(
    'yb-region-selector',
    class YeahboxRegionSelector extends HTMLElement {
      constructor() {
        super();
        this.trigger = this.querySelector('.yb-region-selector__trigger');
        this.dialog = this.querySelector('.yb-region-selector__dialog');
        this.overlay = this.querySelector('.yb-region-selector__overlay');
        this.saveButton = this.querySelector('.yb-region-selector__save');
        this.nativeForm = this.querySelector('.yb-region-selector__form');
        this.countryInput = this.querySelector('[data-region-country-input]');
        this.optionsContainer = this.querySelector('[data-region-options]');
        this.countryValue = this.querySelector('[data-region-country]');
        this.currencyValue = this.querySelector('[data-region-currency]');
        this.languageValue = this.querySelector('[data-region-language]');
        this.languageSelect = this.querySelector('[data-region-language-select]');
        this.summaryFull = this.querySelector('[data-region-summary-full]');
        this.summaryLocale = this.querySelector('[data-region-summary-locale]');
        this.summaryFlag = this.querySelector('[data-region-summary-flag]');
        this.activeTrigger = this.trigger;
        this.isSubmitting = false;
        this.hasRenderedOptions = false;
        this.defaultLanguageLabel = this.dataset.defaultLanguageLabel || '';
        const languageLabels = parseJsonData(this.dataset.regionLanguageLabels, {});
        const regionGroups = parseJsonData(this.dataset.regionGroups, []);
        const regionOptions = parseJsonData(this.dataset.regionOptions, []);
        this.languageLabels =
          languageLabels && typeof languageLabels === 'object' && !Array.isArray(languageLabels)
            ? languageLabels
            : {};
        this.supportedRegionGroups = (Array.isArray(regionGroups) ? regionGroups : []).filter(
          (group) => group && typeof group.id === 'string' && typeof group.label === 'string'
        );
        this.supportedOptions = (Array.isArray(regionOptions) ? regionOptions : [])
          .filter((option) => option && isCountryCode(String(option.countryCode || '').toUpperCase()))
          .map((option) => {
            const countryCode = String(option.countryCode).toUpperCase();
            const languageCode = this.dataset.currentLanguage || getAvailableLanguageCode();

            return {
              countryCode,
              countryName: String(option.countryName || countryCode),
              currencyCode: String(option.currencyCode || '').toUpperCase(),
              currencySymbol: String(option.currencySymbol || ''),
              flag: String(option.flag || getCountryFlag(countryCode)),
              regionGroup: String(option.regionGroup || ''),
              languageCode,
              languageLabel: this.getLanguageLabel(languageCode),
            };
          });
        this.currentCountry = String(this.dataset.currentCountry || '').toUpperCase();
        this.currentCountryName = this.dataset.currentCountryName || this.currentCountry;
        this.currentCurrency = String(this.dataset.currentCurrency || '').toUpperCase();
        this.currentLanguage = this.dataset.currentLanguage || '';
        this.currentLanguageLabel =
          this.dataset.currentLanguageLabel || this.currentLanguage || this.defaultLanguageLabel;
        this.defaultCountry = String(this.dataset.defaultCountry || this.currentCountry).toUpperCase();
        this.selectedOption = this.getInitialOption();
        this.onExternalTriggerClick = this.onExternalTriggerClick.bind(this);
        this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
      }

      connectedCallback() {
        this.trigger?.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle(event.currentTarget);
        });
        const saveTarget = this.nativeForm || this.saveButton;
        saveTarget?.addEventListener(this.nativeForm ? 'submit' : 'click', (event) => {
          event.preventDefault();
          this.submitLocalizationChange();
        });
        this.languageSelect?.addEventListener('change', (event) => {
          const languageCode = availableLanguageLookup.get(String(event.target.value).toLowerCase());
          if (!languageCode || !this.selectedOption) return;
          this.selectedOption = { ...this.selectedOption, languageCode, languageLabel: this.getLanguageLabel(languageCode) };
          this.updateSelectedState();
        });
        this.querySelectorAll('[data-region-close]').forEach((button) => {
          button.addEventListener('click', () => this.close());
        });
        document.addEventListener('click', this.onExternalTriggerClick);
        document.addEventListener('keydown', this.onDocumentKeyDown);
        this.updateSelectedState();
        this.updateSummary();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onExternalTriggerClick);
        document.removeEventListener('keydown', this.onDocumentKeyDown);
      }

      setStorageValue(key, value) {
        try {
          window.localStorage.setItem(key, value);
        } catch (error) {
          // Manual form submission still works if storage is blocked.
        }
      }

      setCookieValue(key, value) {
        try {
          document.cookie = `${key}=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax`;
        } catch (error) {
          // The native localization form remains usable when cookies are blocked.
        }
      }

      getLanguageLabel(languageCode) {
        const normalizedLanguage = String(languageCode || 'en').toLowerCase();
        const baseLanguage = normalizedLanguage.split('-')[0];

        return (
          this.languageLabels[normalizedLanguage] ||
          Array.from(this.languageSelect?.options || []).find((option) => option.value.toLowerCase() === normalizedLanguage)?.textContent ||
          this.languageLabels[baseLanguage] ||
          this.defaultLanguageLabel
        );
      }

      getOption(countryCode) {
        const normalized = String(countryCode || '').toUpperCase();
        const option = this.supportedOptions.find((item) => item.countryCode === normalized);
        if (!option) return null;
        const languageCode = this.selectedOption?.languageCode || this.currentLanguage || option.languageCode;
        return { ...option, languageCode, languageLabel: this.getLanguageLabel(languageCode) };
      }

      getFallbackOption() {
        return this.getOption(this.defaultCountry) || this.supportedOptions[0] || null;
      }

      getCurrentMarketOption() {
        const supportedOption = this.getOption(this.currentCountry);
        if (!this.currentCountry) return null;
        const languageCode = this.currentLanguage || getAvailableLanguageCode();

        return {
          countryCode: this.currentCountry,
          countryName: this.currentCountryName || this.currentCountry,
          currencyCode: this.currentCurrency,
          flag: supportedOption?.flag || '🌐',
          regionGroup: supportedOption?.regionGroup || '',
          languageCode,
          languageLabel: this.currentLanguageLabel || this.getLanguageLabel(languageCode),
        };
      }

      getInitialOption() {
        return this.getCurrentMarketOption() || this.getFallbackOption();
      }

      getCurrentMarketSummary() {
        const option = this.getCurrentMarketOption() || this.getFallbackOption();

        return option ? [option.countryName, option.languageLabel, option.currencyCode].filter(Boolean).join(' · ') : '';
      }

      getCurrentMarketLocaleSummary() {
        const option = this.getCurrentMarketOption() || this.getFallbackOption();

        return option ? [option.countryName, option.languageLabel].filter(Boolean).join(' / ') : '';
      }

      getCurrentMarketFlag() {
        const option = this.getCurrentMarketOption() || this.getFallbackOption();

        return option?.flag || '';
      }

      updateSummary() {
        if (this.summaryFull) this.summaryFull.textContent = this.getCurrentMarketSummary();
        if (this.summaryLocale) this.summaryLocale.textContent = this.getCurrentMarketLocaleSummary();
        if (this.summaryFlag) this.summaryFlag.textContent = this.getCurrentMarketFlag();
      }

      onExternalTriggerClick(event) {
        const trigger = event.target.closest('[data-yb-region-open]');
        if (!trigger) return;
        event.preventDefault();
        this.toggle(trigger);
      }

      onDocumentKeyDown(event) {
        if (event.key === 'Escape' && this.isOpen()) {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key !== 'Tab' || !this.isOpen()) return;
        const focusable = Array.from(this.dialog?.querySelectorAll?.('button:not([disabled]), select:not([disabled]), a[href]') || []);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      isOpen() {
        return Boolean(this.dialog && !this.dialog.hidden);
      }

      toggle(triggeredBy = this.trigger) {
        if (this.isOpen()) {
          this.close();
          return;
        }

        this.open(triggeredBy);
      }

      renderOptions() {
        if (!this.optionsContainer || this.hasRenderedOptions) return;

        const fragment = document.createDocumentFragment();
        this.supportedRegionGroups.forEach((group) => {
          const groupOptions = this.supportedOptions.filter((option) => option.regionGroup === group.id);
          if (!groupOptions.length) return;

          const section = document.createElement('section');
          section.className = 'yb-region-selector__option-group';
          section.setAttribute('aria-label', group.label);

          const heading = document.createElement('h4');
          heading.className = 'yb-region-selector__option-group-title';
          heading.textContent = group.label;
          section.appendChild(heading);

          groupOptions.forEach((option) => {
            const item = document.createElement('button');
            item.type = 'button';
            item.className = 'yb-region-selector__option';
            item.dataset.countryCode = option.countryCode;
            item.setAttribute(
              'aria-pressed',
              option.countryCode === this.selectedOption?.countryCode ? 'true' : 'false'
            );

            const main = document.createElement('span');
            main.className = 'yb-region-selector__option-main';
            const flag = document.createElement('span');
            flag.className = 'yb-region-selector__option-flag';
            flag.textContent = option.flag;
            const countryName = document.createElement('span');
            countryName.className = 'yb-region-selector__option-country';
            countryName.textContent = option.countryName;
            const languageName = document.createElement('span');
            languageName.className = 'yb-region-selector__option-language';
            languageName.textContent = [option.currencyCode, option.currencySymbol].filter(Boolean).join(' ');
            main.append(flag, countryName, languageName);

            item.append(main);

            item.addEventListener('click', () => {
              this.selectedOption = this.getOption(option.countryCode);
              this.updateSelectedState();
            });
            section.appendChild(item);
          });

          fragment.appendChild(section);
        });

        this.optionsContainer.appendChild(fragment);
        this.hasRenderedOptions = true;
      }

      updateSelectedState() {
        const option = this.selectedOption || this.getFallbackOption();
        if (!option) return;
        if (this.countryValue) this.countryValue.textContent = option.countryName;
        if (this.currencyValue) this.currencyValue.textContent = option.currencyCode;
        if (this.languageValue) this.languageValue.textContent = option.languageLabel;
        if (this.languageSelect) this.languageSelect.value = option.languageCode;
        if (this.countryInput) this.countryInput.value = option.countryCode;

        this.querySelectorAll('[data-region-options] .yb-region-selector__option').forEach((item) => {
          item.setAttribute('aria-pressed', item.dataset.countryCode === option.countryCode ? 'true' : 'false');
        });
      }

      setExpandedState(isExpanded) {
        this.trigger?.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        document.querySelectorAll('[data-yb-region-open]').forEach((button) => {
          button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        });
      }

      open(triggeredBy = this.trigger) {
        this.activeTrigger = triggeredBy || this.trigger;
        this.selectedOption = this.getInitialOption();
        this.renderOptions();
        this.updateSelectedState();
        this.dialog?.removeAttribute('hidden');
        this.dialog?.removeAttribute('aria-hidden');
        this.dialog?.removeAttribute('inert');
        this.overlay?.removeAttribute('hidden');
        this.overlay?.removeAttribute('aria-hidden');
        this.overlay?.removeAttribute('inert');
        this.setExpandedState(true);
        document.documentElement.classList.add('yb-region-selector-open');
        const focusTarget = this.querySelector('.yb-region-selector__option[aria-pressed="true"]') ||
          this.querySelector('.yb-region-selector__close');
        focusTarget?.focus();
      }

      close() {
        this.dialog?.setAttribute('hidden', '');
        this.dialog?.setAttribute('aria-hidden', 'true');
        this.dialog?.setAttribute('inert', '');
        this.overlay?.setAttribute('hidden', '');
        this.overlay?.setAttribute('aria-hidden', 'true');
        this.overlay?.setAttribute('inert', '');
        this.setExpandedState(false);
        document.documentElement.classList.remove('yb-region-selector-open');
        this.activeTrigger?.focus();
      }

      setManualPreference(option) {
        this.setStorageValue('yb-localization-manual-choice', 'true');
        this.setStorageValue('yb-localization-country', option.countryCode);
        this.setStorageValue('yb-localization-language', option.languageCode);
        this.setCookieValue('yb-localization-manual-choice', 'true');
        this.setCookieValue('yb-localization-country', option.countryCode);
        this.setCookieValue('yb-localization-language', option.languageCode);
      }

      submitLocalizationChange() {
        if (this.isSubmitting) return;
        const option = this.selectedOption || this.getFallbackOption();
        if (!option) return;

        this.isSubmitting = true;
        this.setManualPreference(option);

        const form = this.nativeForm || document.createElement('form');
        if (!this.nativeForm) {
          form.method = 'POST';
          form.action = this.dataset.localizationPath || '/localization';
          form.acceptCharset = 'UTF-8';
          form.style.display = 'none';
        }

        this.appendHiddenField(form, 'form_type', 'localization');
        this.appendHiddenField(form, 'utf8', '✓');
        this.appendHiddenField(form, '_method', 'put');
        this.appendHiddenField(form, 'country_code', option.countryCode);
        this.appendHiddenField(form, 'language_code', option.languageCode);
        this.appendHiddenField(form, 'return_to', this.getReturnPath(option));

        if (!this.nativeForm) document.body.appendChild(form);
        form.submit();
      }

      getReturnPath(option) {
        let pathname = window.location.pathname;
        const changed = option.countryCode !== this.currentCountry || option.languageCode.toLowerCase() !== this.currentLanguage.toLowerCase();
        if (changed) {
          const firstSegment = pathname.split('/')[1]?.toLowerCase() || '';
          const rootSegment = window.Shopify?.routes?.root?.split('/')[1]?.toLowerCase();
          const routeCodes = new Set(availableLanguageLookup.keys());
          if (['zh', 'zh-cn', 'zh-hans'].some((code) => availableLanguageLookup.has(code))) {
            ['zh', 'zh-cn', 'zh-hans'].forEach((code) => routeCodes.add(code));
          }
          if (['zh-tw', 'zh-hant'].some((code) => availableLanguageLookup.has(code))) {
            ['zh-tw', 'zh-hant'].forEach((code) => routeCodes.add(code));
          }
          const knownLanguage = [...routeCodes].some((code) => {
            const base = code.split('-')[0];
            const suffix = firstSegment.slice(code.length + 1);
            return firstSegment === code || (base !== 'zh' && firstSegment === base) ||
              (firstSegment.startsWith(`${code}-`) && isCountryCode(suffix.toUpperCase()));
          });
          if (knownLanguage || (rootSegment && firstSegment === rootSegment)) {
            const nextSlash = pathname.indexOf('/', 1);
            pathname = nextSlash < 0 ? '/' : pathname.slice(nextSlash);
          }
        }
        const query = String(window.location.search || '').replace(/^\?/, '').split('&').filter((part) => {
          const key = part.split('=', 1)[0];
          try {
            return !['country', 'currency'].includes(decodeURIComponent(key.replace(/\+/g, ' ')).toLowerCase());
          } catch (error) {
            return true;
          }
        }).join('&');
        return `${pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
      }

      appendHiddenField(form, name, value) {
        const existingInput = form.querySelector?.(`[name="${name}"]`);
        const input = existingInput || document.createElement('input');
        if (!existingInput) input.type = 'hidden';
        input.name = name;
        input.value = value;
        if (!existingInput) form.appendChild(input);
      }
    }
  );
}
